/**
 * Organization Detail Page with Time Package Management
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { platformClient, Organization, TimePackage, OrganizationTimeBalance, TimePackagePricing, OrganizationUser } from '../api/platformClient';
import { ArrowLeft, Clock, Package, Plus, AlertCircle, CheckCircle, Settings, Edit2, Save, X, Calendar, Trash2, PlusCircle, MinusCircle, Users, Key, Eye, EyeOff } from 'lucide-react';
import { format } from 'date-fns';
import TopUpModal from '../components/TopUpModal';

type Plan = 'free' | 'pro' | 'enterprise';

const PLAN_DEFAULTS: Record<Plan, { maxRooms: number; maxUsers: number; maxConcurrentListeners: number; maxMonthlyMinutes: number }> = {
  free: { maxRooms: 2, maxUsers: 3, maxConcurrentListeners: 50, maxMonthlyMinutes: 60 },
  pro: { maxRooms: 10, maxUsers: 20, maxConcurrentListeners: 500, maxMonthlyMinutes: 1000 },
  enterprise: { maxRooms: 999, maxUsers: 999, maxConcurrentListeners: 10000, maxMonthlyMinutes: 999999 },
};

export default function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [balance, setBalance] = useState<OrganizationTimeBalance | null>(null);
  const [packages, setPackages] = useState<TimePackage[]>([]);
  const [pricing, setPricing] = useState<TimePackagePricing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Plan editing state
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan>('free');
  const [customLimits, setCustomLimits] = useState({
    maxRooms: 0,
    maxUsers: 0,
    maxConcurrentListeners: 0,
    maxMonthlyMinutes: 0,
  });
  const [subscriptionEndsAt, setSubscriptionEndsAt] = useState<string>('');
  const [isSavingPlan, setIsSavingPlan] = useState(false);

  // Package adjustment state
  const [adjustingPackage, setAdjustingPackage] = useState<TimePackage | null>(null);
  const [adjustmentHours, setAdjustmentHours] = useState<string>('');
  const [adjustmentReason, setAdjustmentReason] = useState<string>('');
  const [isAdjusting, setIsAdjusting] = useState(false);

  // User management state
  const [orgUsers, setOrgUsers] = useState<OrganizationUser[]>([]);
  const [editingUser, setEditingUser] = useState<OrganizationUser | null>(null);
  const [userEditForm, setUserEditForm] = useState({ email: '', password: '', displayName: '' });
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const fetchData = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const [orgsData, balanceData, packagesData, pricingData, usersData] = await Promise.all([
        platformClient.listOrganizations(1, 100),
        platformClient.getOrganizationBalance(id),
        platformClient.listOrganizationPackages(id, true),
        platformClient.getPricing(),
        platformClient.listOrganizationUsers(id),
      ]);

      const org = orgsData.organizations.find(o => o.id === id);
      if (!org) {
        setError('Organization not found');
        return;
      }

      setOrganization(org);
      setBalance(balanceData);
      setPackages(packagesData);
      setPricing(pricingData.packages);
      setOrgUsers(usersData);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleTopUpComplete = () => {
    setShowTopUpModal(false);
    setSuccessMessage('Time package added successfully!');
    fetchData();
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const startEditingPlan = () => {
    if (organization) {
      setSelectedPlan(organization.plan as Plan);
      setCustomLimits({
        maxRooms: organization.maxRooms,
        maxUsers: organization.maxUsers,
        maxConcurrentListeners: 10000, // default, not in org
        maxMonthlyMinutes: organization.maxMonthlyMinutes,
      });
      // Set subscription end date (format for date input: YYYY-MM-DD)
      if (organization.currentPeriodEnd) {
        const date = new Date(organization.currentPeriodEnd);
        setSubscriptionEndsAt(date.toISOString().split('T')[0]);
      } else {
        setSubscriptionEndsAt('');
      }
      setIsEditingPlan(true);
    }
  };

  const cancelEditingPlan = () => {
    setIsEditingPlan(false);
  };

  const handlePlanChange = (plan: Plan) => {
    setSelectedPlan(plan);
    setCustomLimits(PLAN_DEFAULTS[plan]);
  };

  const savePlanChanges = async () => {
    if (!id) return;

    try {
      setIsSavingPlan(true);
      // Convert date string to ISO format, or null to clear
      const endsAt = subscriptionEndsAt ? new Date(subscriptionEndsAt).toISOString() : null;
      await platformClient.updateOrganizationPlan(id, selectedPlan, customLimits, endsAt);
      setSuccessMessage('Plan updated successfully!');
      setIsEditingPlan(false);
      fetchData();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update plan');
    } finally {
      setIsSavingPlan(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!id || !organization) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete "${organization.name}"?\n\nThis action cannot be undone. All data will be permanently deleted.`
    );

    if (confirmed) {
      try {
        await platformClient.deleteOrganization(id);
        setSuccessMessage('Organization deleted successfully!');
        setTimeout(() => navigate('/organizations'), 1500);
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to delete organization');
      }
    }
  };

  const handleDeletePackage = async (pkg: TimePackage) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete this time package?\n\nType: ${pkg.packageType}\nRemaining: ${pkg.hoursRemaining.toFixed(1)} hours\n\nThis action cannot be undone.`
    );

    if (confirmed) {
      try {
        await platformClient.deleteTimePackage(pkg.id, 'Manual deletion by admin');
        setSuccessMessage('Time package deleted successfully!');
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to delete time package');
      }
    }
  };

  const openAdjustModal = (pkg: TimePackage) => {
    setAdjustingPackage(pkg);
    setAdjustmentHours('');
    setAdjustmentReason('');
  };

  const closeAdjustModal = () => {
    setAdjustingPackage(null);
    setAdjustmentHours('');
    setAdjustmentReason('');
  };

  const openEditUserModal = (user: OrganizationUser) => {
    setEditingUser(user);
    setUserEditForm({
      email: user.email,
      password: '',
      displayName: user.displayName || '',
    });
  };

  const closeEditUserModal = () => {
    setEditingUser(null);
    setUserEditForm({ email: '', password: '', displayName: '' });
    setShowPassword(false);
  };

  const handleSaveUser = async () => {
    if (!editingUser || !id) return;

    const updates: { email?: string; password?: string; displayName?: string } = {};

    if (userEditForm.email && userEditForm.email !== editingUser.email) {
      updates.email = userEditForm.email;
    }
    if (userEditForm.password) {
      updates.password = userEditForm.password;
    }
    if (userEditForm.displayName !== (editingUser.displayName || '')) {
      updates.displayName = userEditForm.displayName;
    }

    if (Object.keys(updates).length === 0) {
      setError('No changes to save');
      return;
    }

    try {
      setIsSavingUser(true);
      await platformClient.updateOrganizationUser(id, editingUser.id, updates);
      setSuccessMessage('User updated successfully!');
      closeEditUserModal();
      fetchData();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleAdjustPackage = async () => {
    if (!adjustingPackage || !adjustmentReason.trim()) return;

    const hours = parseFloat(adjustmentHours);
    if (isNaN(hours) || hours === 0) {
      setError('Please enter a valid adjustment amount');
      return;
    }

    try {
      setIsAdjusting(true);
      await platformClient.adjustTimePackageHours(adjustingPackage.id, hours, adjustmentReason);
      setSuccessMessage(`Time package adjusted by ${hours > 0 ? '+' : ''}${hours} hours`);
      closeAdjustModal();
      fetchData();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to adjust time package');
    } finally {
      setIsAdjusting(false);
    }
  };

  if (isLoading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  if (!organization) {
    return <div style={styles.error}>Organization not found</div>;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate('/organizations')} style={styles.backButton}>
          <ArrowLeft size={20} />
        </button>
        <div style={styles.headerInfo}>
          <h1 style={styles.title}>{organization.name}</h1>
          <span style={styles.slug}>{organization.slug}</span>
        </div>
        <span style={{
          ...styles.planBadge,
          backgroundColor: organization.plan === 'enterprise' ? '#22c55e' : organization.plan === 'pro' ? '#6366f1' : '#64748b',
        }}>
          {organization.plan.toUpperCase()}
        </span>
      </div>

      {/* Messages */}
      {successMessage && (
        <div style={styles.success}>
          <CheckCircle size={16} />
          <span>{successMessage}</span>
        </div>
      )}

      {error && (
        <div style={styles.errorBanner}>
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={() => setError('')} style={styles.dismissButton}>×</button>
        </div>
      )}

      {/* Plan Management Card */}
      <div style={styles.planCard}>
        <div style={styles.balanceHeader}>
          <h2 style={styles.sectionTitle}>
            <Settings size={20} />
            Plan Management
          </h2>
          {!isEditingPlan ? (
            <button onClick={startEditingPlan} style={styles.editPlanButton}>
              <Edit2 size={16} />
              Change Plan
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={cancelEditingPlan} style={styles.cancelButton} disabled={isSavingPlan}>
                <X size={16} />
                Cancel
              </button>
              <button onClick={savePlanChanges} style={styles.saveButton} disabled={isSavingPlan}>
                <Save size={16} />
                {isSavingPlan ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>

        {!isEditingPlan ? (
          <div style={styles.planInfo}>
            <div style={styles.planGrid}>
              <div style={styles.planStat}>
                <span style={styles.statLabel}>Current Plan</span>
                <span style={{
                  ...styles.planBadgeLarge,
                  backgroundColor: organization.plan === 'enterprise' ? '#22c55e' : organization.plan === 'pro' ? '#6366f1' : '#64748b',
                }}>
                  {organization.plan.toUpperCase()}
                </span>
              </div>
              <div style={styles.planStat}>
                <span style={styles.statLabel}>Max Rooms</span>
                <span style={styles.statValueLarge}>{organization.maxRooms.toLocaleString()}</span>
              </div>
              <div style={styles.planStat}>
                <span style={styles.statLabel}>Max Users</span>
                <span style={styles.statValueLarge}>{organization.maxUsers.toLocaleString()}</span>
              </div>
              <div style={styles.planStat}>
                <span style={styles.statLabel}>Max Monthly Minutes</span>
                <span style={styles.statValueLarge}>{organization.maxMonthlyMinutes.toLocaleString()}</span>
              </div>
            </div>
            {/* Subscription Expiration */}
            {organization.plan !== 'free' && (
              <div style={styles.expirationInfo}>
                <Calendar size={16} />
                <span style={styles.expirationLabel}>Subscription Expires:</span>
                {organization.currentPeriodEnd ? (
                  <span style={{
                    ...styles.expirationDate,
                    color: new Date(organization.currentPeriodEnd) < new Date() ? '#ef4444' : '#22c55e',
                  }}>
                    {format(new Date(organization.currentPeriodEnd), 'yyyy-MM-dd')}
                    {new Date(organization.currentPeriodEnd) < new Date() && ' (Expired)'}
                  </span>
                ) : (
                  <span style={styles.expirationNone}>Not set (Unlimited)</span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div style={styles.planEditForm}>
            <div style={styles.planSelector}>
              <label style={styles.formLabel}>Select Plan</label>
              <div style={styles.planOptions}>
                {(['free', 'pro', 'enterprise'] as Plan[]).map((plan) => (
                  <button
                    key={plan}
                    onClick={() => handlePlanChange(plan)}
                    style={{
                      ...styles.planOption,
                      backgroundColor: selectedPlan === plan ? '#6366f1' : '#334155',
                      borderColor: selectedPlan === plan ? '#6366f1' : '#475569',
                    }}
                  >
                    <span style={styles.planOptionName}>{plan.toUpperCase()}</span>
                    <span style={styles.planOptionDesc}>
                      {plan === 'free' && '2 Rooms, 3 Users, 60 min/mo'}
                      {plan === 'pro' && '10 Rooms, 20 Users, 1000 min/mo'}
                      {plan === 'enterprise' && 'Unlimited'}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.limitsGrid}>
              <div style={styles.limitInput}>
                <label style={styles.formLabel}>Max Rooms</label>
                <input
                  type="number"
                  value={customLimits.maxRooms}
                  onChange={(e) => setCustomLimits({ ...customLimits, maxRooms: parseInt(e.target.value) || 0 })}
                  style={styles.input}
                />
              </div>
              <div style={styles.limitInput}>
                <label style={styles.formLabel}>Max Users</label>
                <input
                  type="number"
                  value={customLimits.maxUsers}
                  onChange={(e) => setCustomLimits({ ...customLimits, maxUsers: parseInt(e.target.value) || 0 })}
                  style={styles.input}
                />
              </div>
              <div style={styles.limitInput}>
                <label style={styles.formLabel}>Max Concurrent Listeners</label>
                <input
                  type="number"
                  value={customLimits.maxConcurrentListeners}
                  onChange={(e) => setCustomLimits({ ...customLimits, maxConcurrentListeners: parseInt(e.target.value) || 0 })}
                  style={styles.input}
                />
              </div>
              <div style={styles.limitInput}>
                <label style={styles.formLabel}>Max Monthly Minutes</label>
                <input
                  type="number"
                  value={customLimits.maxMonthlyMinutes}
                  onChange={(e) => setCustomLimits({ ...customLimits, maxMonthlyMinutes: parseInt(e.target.value) || 0 })}
                  style={styles.input}
                />
              </div>
            </div>

            {/* Subscription Expiration Date */}
            {selectedPlan !== 'free' && (
              <div style={styles.expirationInput}>
                <label style={styles.formLabel}>
                  <Calendar size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  Subscription Expiration Date
                </label>
                <div style={styles.expirationInputRow}>
                  <input
                    type="date"
                    value={subscriptionEndsAt}
                    onChange={(e) => setSubscriptionEndsAt(e.target.value)}
                    style={styles.dateInput}
                  />
                  <button
                    type="button"
                    onClick={() => setSubscriptionEndsAt('')}
                    style={styles.clearDateButton}
                    title="Clear expiration (set to unlimited)"
                  >
                    Clear
                  </button>
                </div>
                <span style={styles.expirationHint}>
                  Leave empty for unlimited subscription. Set a date for time-limited plans.
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Time Balance Card */}
      <div style={styles.balanceCard}>
        <div style={styles.balanceHeader}>
          <h2 style={styles.sectionTitle}>
            <Clock size={20} />
            Time Balance
          </h2>
          <button onClick={() => setShowTopUpModal(true)} style={styles.topUpButton}>
            <Plus size={16} />
            Top Up
          </button>
        </div>

        <div style={styles.balanceContent}>
          <div style={styles.balanceMain}>
            <span style={styles.balanceValue}>{balance?.totalHoursRemaining.toFixed(1) || '0'}</span>
            <span style={styles.balanceUnit}>hours total remaining</span>
          </div>

          {/* Plan vs Package breakdown */}
          <div style={styles.balanceBreakdown}>
            <div style={styles.breakdownItem}>
              <span style={styles.breakdownLabel}>Plan ({organization.plan})</span>
              <span style={styles.breakdownValue}>{balance?.planHoursRemaining?.toFixed(1) || '0'}h</span>
            </div>
            <div style={styles.breakdownDivider}>+</div>
            <div style={styles.breakdownItem}>
              <span style={styles.breakdownLabel}>Packages</span>
              <span style={styles.breakdownValue}>{balance?.packageHoursRemaining?.toFixed(1) || '0'}h</span>
            </div>
          </div>

          {balance?.nextExpiration && (
            <div style={styles.expirationInfo}>
              <AlertCircle size={14} />
              <span>Next package expiration: {format(new Date(balance.nextExpiration), 'MMM d, yyyy')}</span>
            </div>
          )}
        </div>

        {/* Plan Usage Progress bar */}
        <div style={styles.usageSection}>
          <div style={styles.usageHeader}>
            <span>Plan Usage (used first)</span>
            <span>{organization.minutesUsed} / {organization.maxMonthlyMinutes.toLocaleString()} min</span>
          </div>
          <div style={styles.progressBar}>
            <div
              style={{
                ...styles.progressFill,
                width: `${Math.min(100, (organization.minutesUsed / organization.maxMonthlyMinutes) * 100)}%`,
              }}
            />
          </div>
          <div style={styles.usageNote}>
            Plan minutes are used first. When exhausted, package hours are used.
          </div>
        </div>
      </div>

      {/* Active Packages */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <Package size={20} />
          Time Packages
        </h2>

        <div style={styles.packageList}>
          {packages.map((pkg) => (
            <div key={pkg.id} style={styles.packageCard}>
              <div style={styles.packageHeader}>
                <span style={styles.packageType}>{pkg.packageType}</span>
                <span style={{
                  ...styles.statusBadge,
                  backgroundColor: pkg.status === 'active' ? '#22c55e20' : pkg.status === 'depleted' ? '#ef444420' : '#f59e0b20',
                  color: pkg.status === 'active' ? '#22c55e' : pkg.status === 'depleted' ? '#ef4444' : '#f59e0b',
                }}>
                  {pkg.status}
                </span>
              </div>

              <div style={styles.packageBody}>
                <div style={styles.packageStat}>
                  <span style={styles.statLabel}>Remaining</span>
                  <span style={styles.statValue}>{pkg.hoursRemaining.toFixed(1)} / {pkg.hoursPurchased} hours</span>
                </div>
                <div style={styles.packageStat}>
                  <span style={styles.statLabel}>Expires</span>
                  <span style={styles.statValue}>{format(new Date(pkg.expiresAt), 'MMM d, yyyy')}</span>
                </div>
                <div style={styles.packageStat}>
                  <span style={styles.statLabel}>Price</span>
                  <span style={styles.statValue}>₩{pkg.totalPrice.toLocaleString()}</span>
                </div>
              </div>

              {pkg.depositorName && (
                <div style={styles.packageMeta}>
                  <span>Depositor: {pkg.depositorName}</span>
                  {pkg.transferDate && <span> | {pkg.transferDate}</span>}
                </div>
              )}

              {/* Package Actions */}
              <div style={styles.packageActions}>
                <button
                  onClick={() => openAdjustModal(pkg)}
                  style={styles.adjustButton}
                  title="Adjust hours"
                >
                  <PlusCircle size={14} />
                  Adjust Hours
                </button>
                <button
                  onClick={() => handleDeletePackage(pkg)}
                  style={styles.deletePackageButton}
                  title="Delete package"
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            </div>
          ))}

          {packages.length === 0 && (
            <div style={styles.empty}>
              <Package size={32} color="#64748b" />
              <p>No time packages yet</p>
              <button onClick={() => setShowTopUpModal(true)} style={styles.emptyButton}>
                Add First Package
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Organization Users */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <Users size={20} />
          Organization Users
        </h2>

        <div style={styles.userList}>
          {orgUsers.map((user) => (
            <div key={user.id} style={styles.userCard}>
              <div style={styles.userInfo}>
                <div style={styles.userAvatar}>
                  {(user.displayName || user.email).charAt(0).toUpperCase()}
                </div>
                <div style={styles.userDetails}>
                  <div style={styles.userName}>
                    {user.displayName || user.email.split('@')[0]}
                  </div>
                  <div style={styles.userEmail}>{user.email}</div>
                </div>
              </div>
              <div style={styles.userMeta}>
                <span style={{
                  ...styles.roleBadge,
                  backgroundColor: user.role === 'owner' ? '#7c3aed20' : '#6366f120',
                  color: user.role === 'owner' ? '#a78bfa' : '#818cf8',
                }}>
                  {user.role.toUpperCase()}
                </span>
                <span style={{
                  ...styles.statusBadge,
                  backgroundColor: user.status === 'active' ? '#22c55e20' : '#ef444420',
                  color: user.status === 'active' ? '#22c55e' : '#ef4444',
                }}>
                  {user.status}
                </span>
              </div>
              <button
                onClick={() => openEditUserModal(user)}
                style={styles.editUserButton}
              >
                <Key size={14} />
                Edit Credentials
              </button>
            </div>
          ))}

          {orgUsers.length === 0 && (
            <div style={styles.empty}>
              <Users size={32} color="#64748b" />
              <p>No users found</p>
            </div>
          )}
        </div>
      </div>

      {/* Danger Zone */}
      <div style={styles.dangerZone}>
        <h2 style={styles.dangerTitle}>Danger Zone</h2>
        <div style={styles.dangerContent}>
          <div style={styles.dangerItem}>
            <div>
              <strong style={styles.dangerItemTitle}>Delete Organization</strong>
              <p style={styles.dangerItemDesc}>
                Permanently delete this organization and all its data. This action cannot be undone.
              </p>
            </div>
            <button onClick={handleDeleteOrganization} style={styles.deleteButton}>
              Delete Organization
            </button>
          </div>
        </div>
      </div>

      {/* Top Up Modal */}
      {showTopUpModal && (
        <TopUpModal
          organization={organization}
          pricing={pricing}
          onClose={() => setShowTopUpModal(false)}
          onComplete={handleTopUpComplete}
        />
      )}

      {/* Adjust Package Modal */}
      {adjustingPackage && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Adjust Time Package</h3>
              <button onClick={closeAdjustModal} style={styles.modalCloseButton}>
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.modalInfo}>
                <p><strong>Package:</strong> {adjustingPackage.packageType}</p>
                <p><strong>Current Hours:</strong> {adjustingPackage.hoursRemaining.toFixed(2)} / {adjustingPackage.hoursPurchased} hours</p>
              </div>

              <div style={styles.adjustInputGroup}>
                <label style={styles.formLabel}>
                  Hours Adjustment
                  <span style={styles.adjustHint}>(Use negative to subtract)</span>
                </label>
                <div style={styles.adjustInputRow}>
                  <button
                    type="button"
                    onClick={() => setAdjustmentHours(prev => String((parseFloat(prev) || 0) - 1))}
                    style={styles.adjustStepButton}
                  >
                    <MinusCircle size={20} />
                  </button>
                  <input
                    type="number"
                    value={adjustmentHours}
                    onChange={(e) => setAdjustmentHours(e.target.value)}
                    placeholder="0"
                    step="0.5"
                    style={styles.adjustInput}
                  />
                  <button
                    type="button"
                    onClick={() => setAdjustmentHours(prev => String((parseFloat(prev) || 0) + 1))}
                    style={styles.adjustStepButton}
                  >
                    <PlusCircle size={20} />
                  </button>
                </div>
                {adjustmentHours && (
                  <p style={styles.adjustPreview}>
                    New balance: {(adjustingPackage.hoursRemaining + (parseFloat(adjustmentHours) || 0)).toFixed(2)} hours
                  </p>
                )}
              </div>

              <div style={styles.adjustInputGroup}>
                <label style={styles.formLabel}>Reason (required)</label>
                <textarea
                  value={adjustmentReason}
                  onChange={(e) => setAdjustmentReason(e.target.value)}
                  placeholder="Explain the reason for this adjustment..."
                  style={styles.adjustTextarea}
                  rows={3}
                />
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button onClick={closeAdjustModal} style={styles.cancelButton} disabled={isAdjusting}>
                Cancel
              </button>
              <button
                onClick={handleAdjustPackage}
                style={styles.saveButton}
                disabled={isAdjusting || !adjustmentReason.trim() || !adjustmentHours}
              >
                {isAdjusting ? 'Adjusting...' : 'Apply Adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Edit User Credentials</h3>
              <button onClick={closeEditUserModal} style={styles.modalCloseButton}>
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.modalInfo}>
                <p><strong>User:</strong> {editingUser.displayName || editingUser.email}</p>
                <p><strong>Role:</strong> {editingUser.role.toUpperCase()}</p>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Email</label>
                <input
                  type="email"
                  value={userEditForm.email}
                  onChange={(e) => setUserEditForm({ ...userEditForm, email: e.target.value })}
                  style={styles.input}
                  placeholder="user@example.com"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Display Name</label>
                <input
                  type="text"
                  value={userEditForm.displayName}
                  onChange={(e) => setUserEditForm({ ...userEditForm, displayName: e.target.value })}
                  style={styles.input}
                  placeholder="User Name"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  New Password
                  <span style={styles.passwordHint}>(Leave empty to keep current)</span>
                </label>
                <div style={styles.inputWrapper}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={userEditForm.password}
                    onChange={(e) => setUserEditForm({ ...userEditForm, password: e.target.value })}
                    style={styles.inputWithIcon}
                    placeholder="New password (min 6 characters)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={styles.eyeButton}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button onClick={closeEditUserModal} style={styles.cancelButton} disabled={isSavingUser}>
                Cancel
              </button>
              <button
                onClick={handleSaveUser}
                style={styles.saveButton}
                disabled={isSavingUser}
              >
                {isSavingUser ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '2rem',
    maxWidth: '900px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '2rem',
  },
  backButton: {
    backgroundColor: '#334155',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem',
    color: '#94a3b8',
    cursor: 'pointer',
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    color: '#f8fafc',
    fontSize: '1.5rem',
    fontWeight: 700,
    margin: 0,
  },
  slug: {
    color: '#64748b',
    fontSize: '0.875rem',
  },
  planBadge: {
    color: 'white',
    padding: '0.5rem 1rem',
    borderRadius: '999px',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  loading: {
    color: '#94a3b8',
    padding: '2rem',
    textAlign: 'center',
  },
  error: {
    color: '#fecaca',
    backgroundColor: '#7f1d1d',
    padding: '1rem',
    borderRadius: '8px',
    margin: '2rem',
  },
  success: {
    backgroundColor: '#14532d',
    color: '#86efac',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  },
  balanceCard: {
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  balanceHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    color: '#f8fafc',
    fontSize: '1rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    margin: 0,
  },
  topUpButton: {
    backgroundColor: '#6366f1',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  balanceContent: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginBottom: '1.5rem',
  },
  balanceMain: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '0.5rem',
  },
  balanceValue: {
    color: '#f8fafc',
    fontSize: '3rem',
    fontWeight: 700,
    lineHeight: 1,
  },
  balanceUnit: {
    color: '#94a3b8',
    fontSize: '1rem',
  },
  expirationInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#94a3b8',
    fontSize: '0.875rem',
    marginTop: '1rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#334155',
    borderRadius: '8px',
  },
  balanceBreakdown: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: '#334155',
    borderRadius: '8px',
  },
  breakdownItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '0.25rem',
  },
  breakdownLabel: {
    color: '#94a3b8',
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
  },
  breakdownValue: {
    color: '#f8fafc',
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  breakdownDivider: {
    color: '#64748b',
    fontSize: '1.25rem',
    fontWeight: 500,
  },
  usageNote: {
    color: '#64748b',
    fontSize: '0.75rem',
    marginTop: '0.5rem',
    fontStyle: 'italic' as const,
  },
  expirationLabel: {
    color: '#94a3b8',
  },
  expirationDate: {
    fontWeight: 600,
  },
  expirationNone: {
    color: '#64748b',
    fontStyle: 'italic',
  },
  expirationInput: {
    marginTop: '0.5rem',
  },
  expirationInputRow: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '0.5rem',
  },
  dateInput: {
    flex: 1,
    padding: '0.75rem',
    borderRadius: '8px',
    border: '1px solid #475569',
    backgroundColor: '#334155',
    color: '#f8fafc',
    fontSize: '1rem',
  },
  clearDateButton: {
    padding: '0.75rem 1rem',
    backgroundColor: '#475569',
    color: '#94a3b8',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
  expirationHint: {
    display: 'block',
    marginTop: '0.5rem',
    fontSize: '0.75rem',
    color: '#64748b',
  },
  usageSection: {},
  usageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    color: '#94a3b8',
    fontSize: '0.875rem',
    marginBottom: '0.5rem',
  },
  progressBar: {
    height: '8px',
    backgroundColor: '#334155',
    borderRadius: '999px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#6366f1',
    borderRadius: '999px',
    transition: 'width 0.3s',
  },
  section: {
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    padding: '1.5rem',
  },
  packageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginTop: '1rem',
  },
  packageCard: {
    backgroundColor: '#334155',
    borderRadius: '8px',
    padding: '1rem',
  },
  packageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.75rem',
  },
  packageType: {
    color: '#f8fafc',
    fontSize: '1rem',
    fontWeight: 600,
  },
  statusBadge: {
    padding: '0.25rem 0.75rem',
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  packageBody: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1rem',
  },
  packageStat: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  statLabel: {
    color: '#64748b',
    fontSize: '0.75rem',
  },
  statValue: {
    color: '#e2e8f0',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  packageMeta: {
    marginTop: '0.75rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid #475569',
    color: '#94a3b8',
    fontSize: '0.75rem',
  },
  empty: {
    textAlign: 'center',
    padding: '2rem',
    color: '#64748b',
  },
  emptyButton: {
    backgroundColor: '#6366f1',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    marginTop: '1rem',
    cursor: 'pointer',
  },
  // Plan Management Styles
  planCard: {
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  editPlanButton: {
    backgroundColor: '#475569',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  cancelButton: {
    backgroundColor: '#475569',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
  },
  saveButton: {
    backgroundColor: '#22c55e',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  planInfo: {},
  planGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '1.5rem',
  },
  planStat: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  planBadgeLarge: {
    color: 'white',
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    textAlign: 'center',
  },
  statValueLarge: {
    color: '#f8fafc',
    fontSize: '1.5rem',
    fontWeight: 700,
  },
  planEditForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  planSelector: {},
  formLabel: {
    color: '#94a3b8',
    fontSize: '0.875rem',
    marginBottom: '0.5rem',
    display: 'block',
  },
  planOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1rem',
  },
  planOption: {
    padding: '1rem',
    borderRadius: '8px',
    border: '2px solid',
    cursor: 'pointer',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  planOptionName: {
    color: 'white',
    fontSize: '1rem',
    fontWeight: 600,
  },
  planOptionDesc: {
    color: '#94a3b8',
    fontSize: '0.75rem',
  },
  limitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1rem',
  },
  limitInput: {},
  input: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '8px',
    border: '1px solid #475569',
    backgroundColor: '#334155',
    color: '#f8fafc',
    fontSize: '1rem',
  },
  inputWrapper: {
    position: 'relative' as const,
  },
  inputWithIcon: {
    width: '100%',
    padding: '0.75rem 2.5rem 0.75rem 0.75rem',
    borderRadius: '8px',
    border: '1px solid #475569',
    backgroundColor: '#334155',
    color: '#f8fafc',
    fontSize: '1rem',
    boxSizing: 'border-box' as const,
  },
  eyeButton: {
    position: 'absolute' as const,
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
  },
  // Danger Zone Styles
  dangerZone: {
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    padding: '1.5rem',
    marginTop: '1.5rem',
    border: '1px solid #7f1d1d',
  },
  dangerTitle: {
    color: '#ef4444',
    fontSize: '1rem',
    fontWeight: 600,
    margin: '0 0 1rem 0',
  },
  dangerContent: {},
  dangerItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
  },
  dangerItemTitle: {
    color: '#f8fafc',
    fontSize: '0.875rem',
  },
  dangerItemDesc: {
    color: '#94a3b8',
    fontSize: '0.75rem',
    margin: '0.25rem 0 0 0',
  },
  deleteButton: {
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  errorBanner: {
    backgroundColor: '#7f1d1d',
    color: '#fecaca',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  },
  dismissButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: 'inherit',
    fontSize: '1.25rem',
    cursor: 'pointer',
    marginLeft: 'auto',
  },
  // Package Actions Styles
  packageActions: {
    marginTop: '0.75rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid #475569',
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
  },
  adjustButton: {
    backgroundColor: '#475569',
    color: '#f8fafc',
    border: 'none',
    borderRadius: '6px',
    padding: '0.4rem 0.75rem',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    cursor: 'pointer',
  },
  deletePackageButton: {
    backgroundColor: 'transparent',
    color: '#ef4444',
    border: '1px solid #ef4444',
    borderRadius: '6px',
    padding: '0.4rem 0.75rem',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    cursor: 'pointer',
  },
  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '480px',
    margin: '1rem',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid #334155',
  },
  modalTitle: {
    color: '#f8fafc',
    fontSize: '1.125rem',
    fontWeight: 600,
    margin: 0,
  },
  modalCloseButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '0.25rem',
    borderRadius: '4px',
  },
  modalBody: {
    padding: '1.5rem',
  },
  modalInfo: {
    backgroundColor: '#334155',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    color: '#e2e8f0',
    fontSize: '0.875rem',
  },
  adjustInputGroup: {
    marginBottom: '1rem',
  },
  adjustHint: {
    color: '#64748b',
    fontSize: '0.75rem',
    marginLeft: '0.5rem',
  },
  adjustInputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.5rem',
  },
  adjustStepButton: {
    backgroundColor: '#475569',
    color: '#f8fafc',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  adjustInput: {
    flex: 1,
    padding: '0.75rem',
    borderRadius: '8px',
    border: '1px solid #475569',
    backgroundColor: '#334155',
    color: '#f8fafc',
    fontSize: '1.25rem',
    textAlign: 'center',
    fontWeight: 600,
  },
  adjustPreview: {
    marginTop: '0.5rem',
    color: '#6366f1',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  adjustTextarea: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '8px',
    border: '1px solid #475569',
    backgroundColor: '#334155',
    color: '#f8fafc',
    fontSize: '0.875rem',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #334155',
  },
  // User Management Styles
  userList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  userCard: {
    backgroundColor: '#334155',
    borderRadius: '8px',
    padding: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flex: 1,
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#6366f1',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '1rem',
  },
  userDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  userName: {
    color: '#f8fafc',
    fontWeight: 500,
    fontSize: '0.9rem',
  },
  userEmail: {
    color: '#94a3b8',
    fontSize: '0.75rem',
  },
  userMeta: {
    display: 'flex',
    gap: '0.5rem',
  },
  roleBadge: {
    padding: '0.25rem 0.75rem',
    borderRadius: '999px',
    fontSize: '0.7rem',
    fontWeight: 600,
  },
  editUserButton: {
    backgroundColor: '#6366f1',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '0.5rem 0.75rem',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    cursor: 'pointer',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  passwordHint: {
    color: '#64748b',
    fontSize: '0.75rem',
    marginLeft: '0.5rem',
    fontWeight: 'normal',
  },
};
